import * as React from "react";
import { create } from "zustand";

import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastProps,
} from "./toast";

interface ToastEntry {
  id: number;
  title?: string;
  description?: string;
  variant?: ToastProps["variant"];
}

interface ToastState {
  toasts: ToastEntry[];
  push: (entry: Omit<ToastEntry, "id">) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (entry) => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { ...entry, id }] }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const useToast = (): {
  toast: (entry: Omit<ToastEntry, "id">) => void;
  dismiss: (id: number) => void;
} => {
  const push = useToastStore((s) => s.push);
  const dismiss = useToastStore((s) => s.dismiss);
  return {
    toast: (entry) => {
      push(entry);
    },
    dismiss,
  };
};

export const Toaster: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <ToastProvider swipeDirection="right" duration={5000}>
      {toasts.map((t) => (
        <Toast
          key={t.id}
          {...(t.variant ? { variant: t.variant } : {})}
          onOpenChange={(open) => {
            if (!open) dismiss(t.id);
          }}
        >
          <div className="flex flex-col gap-1">
            {t.title && <ToastTitle>{t.title}</ToastTitle>}
            {t.description && (
              <ToastDescription>{t.description}</ToastDescription>
            )}
          </div>
          <ToastClose />
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
};
